import { Suspense, useState } from 'react';

import { getCountry } from '@iwIntlProvider/CountryProvider';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconAngleRight } from '@iwoca/icons';
import { Button, Modal } from '@iwoca/orion';
import { format, differenceInDays } from 'date-fns';
import { de as deLocale } from 'date-fns/locale/de';
import { enGB as enLocale } from 'date-fns/locale/en-GB';
import { FormattedMessage } from 'react-intl';

import { useRCFNextRepayment } from './useRCFNextRepayment';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { RCFRepaymentSchedule } from '../../../myOffers/approvedView/RCFApprovedView/RCFRepaymentSchedule/RCFRepaymentSchedule';
import DashboardCTA from '../../dashboardComponents/DashboardCTA/DashboardCTA';
import DateInIcon from '../futureRepayment/DateInIcon';

export const RCFNextRepayment = ({ accountId }: { accountId: string }) => {
    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense fallback={<div>loading...</div>}>
                <NextRepayment accountId={accountId} />
            </Suspense>
        </ErrorBoundary>
    );
};

const NextRepayment = ({ accountId }: { accountId: string }) => {
    const {
        schedule,
        scheduleSummary,
        outstandingBalance,
        nextRepaymentDate,
        nextRepaymentAmount
    } = useRCFNextRepayment(accountId);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const isInRepaymentFreePeriod = Boolean(
        schedule.find((repayment) => repayment.principal === 0)
    );

    let dateFnsLocale = enLocale;
    if (getCountry() === 'DE') {
        dateFnsLocale = deLocale;
    }

    const month = format(nextRepaymentDate, 'MMM', { locale: dateFnsLocale });
    const day = format(nextRepaymentDate, 'd');

    const daysUntilNextRepayment = differenceInDays(
        new Date(nextRepaymentDate),
        new Date()
    );

    return (
        <>
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                closeIcon
            >
                <RCFRepaymentSchedule
                    repaymentSchedule={{
                        schedule: schedule,
                        summary: scheduleSummary
                    }}
                    isInRepaymentFreePeriod={isInRepaymentFreePeriod}
                    newOutstandingBalance={outstandingBalance}
                />
            </Modal>

            <DashboardCTA
                icon={<DateInIcon month={month} date={day} />}
                title={
                    <FormattedMessage
                        id="_.outstanding_view.rcf_next_repayment_title"
                        defaultMessage="Next repayment"
                    />
                }
                description={
                    <span data-testid="rcf-next-repayment-description">
                        <FormattedMessage
                            id="_.outstanding_view.rcf_next_repayment_description"
                            defaultMessage="In {daysUntilNextRepayment, plural, one {# day} other {# days}}, we will debit {nextRepaymentAmount} from your account."
                            values={{
                                daysUntilNextRepayment,
                                nextRepaymentAmount: (
                                    <FormattedCurrency
                                        value={nextRepaymentAmount}
                                    />
                                )
                            }}
                        />
                    </span>
                }
                cta={
                    <Button
                        variant="tertiary"
                        onClick={() => {
                            tracking.ampli.rcfViewRepaymentPlanClicked();
                            setIsModalOpen(true);
                        }}
                    >
                        <FormattedMessage
                            id="_.outstanding_view.rcf_next_repayment_cta"
                            defaultMessage="View payment plan"
                        />{' '}
                        <IconAngleRight />
                    </Button>
                }
            />
        </>
    );
};
